import type { RoiId } from '~/domain/changes/ids';
import {
    Box,
    Checkbox,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { RoiFieldEdits } from '~/hooks/export/useExportEditing';
import StructureRow from './StructureRow';

type StructuresTableProps = {
    isLoading?: boolean;
    structures: ReadonlyArray<RoiReadModel>;
    selectedRoiIds: RoiId[];
    onSelect: (roiId: RoiId) => void;
    ssUid: string;
    effectiveIsApproved: boolean;
    ssApprovalEdit?: DICOMApprovalStatus;
    roiEdits: Partial<Record<RoiId, RoiFieldEdits>>;
    setRoiName: (roiId: RoiId, name: string) => void;
    setRoiType: (roiId: RoiId, type: RoiInterpretedType) => void;
};

const TableLoader = () => (
    <TableRow>
        <TableCell colSpan={3} align="center" height={80}>
            <CircularProgress size={24} />
            <Typography variant="body2" mt={0.5}>
                Loading datasets...
            </Typography>
        </TableCell>
    </TableRow>
);

const StructuresTable = ({
    isLoading,
    structures,
    selectedRoiIds,
    onSelect,
    ssUid,
    effectiveIsApproved,
    ssApprovalEdit,
    roiEdits,
    setRoiName,
    setRoiType,
}: StructuresTableProps) => {
    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;
        structures.forEach((structure) => {
            const isSelected = selectedRoiIds.includes(structure.roiId);
            if (isChecked && !isSelected) {
                onSelect(structure.roiId);
            } else if (!isChecked && isSelected) {
                onSelect(structure.roiId);
            }
        });
    };

    return (
        <TableContainer
            sx={(theme) => ({
                height: '200px',
                overflowY: 'auto',
                borderRadius: `${theme.shape.borderRadius}px`,
                border: `1px solid ${theme.palette.divider}`,
            })}
        >
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow>
                        <TableCell
                            align="left"
                            sx={{ width: '40%', paddingLeft: 0.5 }}
                        >
                            <Box display="flex" alignItems={'center'}>
                                <Checkbox
                                    size="small"
                                    color="primary"
                                    sx={{ mr: 0.5 }}
                                    checked={
                                        selectedRoiIds.length ===
                                        structures.length
                                    }
                                    indeterminate={
                                        selectedRoiIds.length > 0 &&
                                        selectedRoiIds.length <
                                            structures.length
                                    }
                                    onChange={handleSelectAll}
                                />
                                STRUCTURE{' '}
                                <Typography
                                    sx={{ ml: 0.5 }}
                                    fontSize={12}
                                    variant="body2"
                                >
                                    ({selectedRoiIds.length})
                                </Typography>
                            </Box>
                        </TableCell>
                        <TableCell align="left" sx={{ width: '20%' }}>
                            TYPE
                        </TableCell>
                        <TableCell align="right" sx={{ width: '30%' }}>
                            STATUS
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {isLoading ? (
                        <TableLoader />
                    ) : (
                        structures.map((structure) => (
                            <StructureRow
                                key={structure.roiId}
                                structure={structure}
                                onSelect={onSelect}
                                selectedRoiIds={selectedRoiIds}
                                ssUid={ssUid}
                                effectiveIsApproved={effectiveIsApproved}
                                ssApprovalEdit={ssApprovalEdit}
                                roiEdits={roiEdits[structure.roiId] ?? {}}
                                setRoiName={(name) =>
                                    setRoiName(structure.roiId, name)
                                }
                                setRoiType={(type) =>
                                    setRoiType(structure.roiId, type)
                                }
                            />
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default StructuresTable;
